import { compact, keyBy, map } from 'lodash'
import { useMemo } from 'react'

import { isSharedOrg, type CustomerWithPath, type OrgPath } from '~/data/types/organization'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { Customer } from '../../client/fleet-management'
import useDataManager from './useDataManager'

export default function useManagedOrgTree() {
  const principalOrgId = usePrincipalOrgId()
  const { data: orgs, isLoading, isFetching, isFromApi } = useDataManager().customers

  const orgTree = useMemo(() => new OrgTree(principalOrgId, orgs), [principalOrgId, orgs])
  return { orgTree, isLoading, isFetching, isFromApi }
}

type TreeNode = CustomerWithPath & {
  children: TreeNode[]
}

class OrgTree {
  private readonly root: TreeNode | null
  // Shared org IDs (those flagged with the SHARED sentinel) — surfaced flat;
  // not part of the real hierarchy. They are accessible from the principal
  // org's perspective only and are appended to the principal's descendantIds
  // set so the same filter logic that handles real children works for them.
  private readonly sharedOrgIds: string[]
  // `descendants` reflects the real hierarchy only — shared orgs are appended to
  // `descendantIds` for the principal but never attached here (no TreeNode).
  public readonly indexes = {
    descendants: new Map<string, TreeNode[]>(),
    descendantIds: new Map<string, Set<string>>(),
    // Keyed off the raw payload rather than the tree, so orgs with no TreeNode
    // (shared orgs, and anything outside the principal's subtree) still resolve.
    byId: new Map<string, Customer>()
  }

  constructor(principalOrgId: string, orgs: Customer[]) {
    for (const org of orgs) {
      if (org.id) this.indexes.byId.set(org.id, org)
    }
    this.sharedOrgIds = compact(orgs.filter(isSharedOrg).map((org) => org.id))
    this.root = this.buildTree(principalOrgId, orgs)
    if (this.root) {
      this.buildDescendantsIndex(this.root)
      this.buildDescendantsIdIndex(principalOrgId)
    }
  }

  private buildTree(principalOrgId: string, orgs: Customer[]): TreeNode | null {
    if (!principalOrgId) return null
    const orgMap = keyBy(orgs, 'id')
    const nodes = new Map<string, TreeNode>([[principalOrgId, { id: principalOrgId, children: [], path: [] }]])
    const childrenMap = new Map<string, TreeNode[]>()

    const getOrCreateNode = (id: string) => {
      let node = nodes.get(id)
      if (!node) {
        const org = orgMap[id]
        node = {
          ...(org ?? { id }),
          path: [],
          children: []
        }
        nodes.set(id, node)
      }
      return node
    }

    for (const org of orgs) {
      if (!org.id || !org.parentOrganizationId) continue
      // Shared orgs are not part of the real hierarchy — skip the parent link
      // so they don't get attached to the SHARED sentinel as a phantom parent.
      if (isSharedOrg(org)) continue

      const childNode = getOrCreateNode(org.id)

      if (!childrenMap.has(org.parentOrganizationId)) {
        childrenMap.set(org.parentOrganizationId, [])
      }

      childrenMap.get(org.parentOrganizationId)!.push(childNode)
    }

    for (const [parentId, children] of childrenMap.entries()) {
      const parentNode = getOrCreateNode(parentId)
      parentNode.children = children
    }

    const root = getOrCreateNode(principalOrgId)

    const assignPaths = (node: TreeNode, parentPath: OrgPath) => {
      if (!node.id) return

      const nodePath = [...parentPath, { id: node.id, name: node.organizationNm ?? '' }]
      node.path = nodePath
      node.children.forEach((child) => {
        assignPaths(child, nodePath)
      })
    }

    assignPaths(root, [])
    return root
  }

  /**
   * The organization and everything genuinely beneath it, excluding anything
   * reached through a share.
   *
   * `descendantIds` deliberately folds shared org IDs into the principal's set
   * so visibility filters treat them like children. A containment question
   * must not: a share grants access without placing the org under the grantee,
   * so a shared org's site is not a site this organization contains.
   */
  public realHierarchyIds(orgId: string): Set<string> {
    const ids = new Set(compact(map(this.indexes.descendants.get(orgId) ?? [], 'id')))
    // An organization always contains itself, including one the index has no
    // entry for. A shared org is queried as itself but never attached to the
    // tree, so without this its own sites would be filtered away too.
    ids.add(orgId)
    return ids
  }

  private buildDescendantsIndex(root: TreeNode) {
    root.children.forEach((child) => {
      this.buildDescendantsIndex(child)
    })

    const initialValues = [root]
    const descendants = root.children.reduce((acc, child) => {
      if (!child.id) return acc

      const childSet = this.indexes.descendants.get(child.id)
      if (childSet) {
        return [...acc, ...childSet]
      }

      acc.push(child)
      return acc
    }, initialValues)

    if (root.id) {
      this.indexes.descendants.set(root.id, descendants)
    }
  }

  private buildDescendantsIdIndex(principalOrgId: string) {
    for (const [id, descendants] of this.indexes.descendants.entries()) {
      const ids = compact(map(descendants, 'id'))
      this.indexes.descendantIds.set(id, new Set(ids))
    }

    // Shared orgs are surfaced under the org that received the share — i.e.,
    // the principal (the manager-layer customers cache is fetched for the
    // principal). Add them to the principal's descendant ID set so the same
    // filter logic that gates vehicle/site visibility includes shared orgs.
    // Shared orgs are NOT recursively expanded — only the literal IDs are
    // added, matching the explicit-only invariant in the backend.
    if (this.sharedOrgIds.length > 0 && principalOrgId) {
      const principalSet = this.indexes.descendantIds.get(principalOrgId) ?? new Set()
      this.sharedOrgIds.forEach((id) => principalSet.add(id))
      this.indexes.descendantIds.set(principalOrgId, principalSet)
    }
  }
}
